import type { BenchmarkInput } from '../../api';
import { parseGermanNumber } from '../../format';

/**
 * Reading benchmark figures pasted from a spreadsheet or a CSV file. The first
 * row names the columns - in any order, only "Bezeichnung" is required. Tabs
 * (what Excel puts on the clipboard) or semicolons separate the columns.
 * Values the header does not name are taken from `defaults`, e.g. the source
 * and edition that hold for the whole table.
 */
export interface BenchmarkPaste {
  rows: BenchmarkInput[];
  /** Line numbers (1-based) that could not be read, with the reason. */
  problems: { line: number; reason: string }[];
  /** Column headings that were not recognised. */
  unknown: string[];
}

type Field = keyof BenchmarkInput;

/** Byte order mark Excel puts in front of CSV files; written as a code point, since it is invisible. */
const BOM = String.fromCharCode(0xfeff);

const HEADINGS: [RegExp, Field][] = [
  [/^quelle$/, 'source'],
  [/^(ausgabe|jahr|ausgabe\/jahr|ausgabe \/ jahr|edition)$/, 'edition'],
  [/^preisstand$/, 'priceDate'],
  [/^(region|regionalfaktor|ort)$/, 'region'],
  [/^(gebäudeart|gebaeudeart|objektart|gebäudetyp|nutzung)$/, 'buildingType'],
  [/^(kg|kostengruppe|din 276)$/, 'costGroup'],
  [/^(bezeichnung|text|kurztext|element)$/, 'label'],
  [/^(einheit|me|bezugseinheit)$/, 'unit'],
  [/^(von|min|minimum|unten)$/, 'low'],
  [/^(mittel|mittelwert|mw|ø|durchschnitt)$/, 'mid'],
  [/^(bis|max|maximum|oben)$/, 'high'],
  [/^(lizenz|lizenzkennzeichnung|nutzungsrecht)$/, 'license'],
  [/^(bemerkung|notiz|hinweis|anmerkung)$/, 'note'],
];
const NUMERIC = new Set<Field>(['low', 'mid', 'high']);

function split(line: string, tab: boolean): string[] {
  if (tab) return line.split('\t').map((c) => c.trim());
  // Semicolon CSV with optional quotes.
  const out: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ';') { out.push(cell.trim()); cell = ''; }
    else cell += ch;
  }
  out.push(cell.trim());
  return out;
}

export function parsePastedBenchmarks(input: string, defaults: Partial<BenchmarkInput> = {}): BenchmarkPaste {
  const lines = (input.startsWith(BOM) ? input.slice(1) : input).replace(/\r/g, '').split('\n');
  const first = lines.findIndex((l) => l.trim() !== '');
  if (first < 0) return { rows: [], problems: [], unknown: [] };
  const tab = lines[first]!.includes('\t');
  const heads = split(lines[first]!, tab);
  const fields: (Field | null)[] = heads.map((h) => HEADINGS.find(([re]) => re.test(h.toLowerCase()))?.[1] ?? null);
  const unknown = heads.filter((h, i) => h !== '' && fields[i] === null);
  if (!fields.includes('label')) {
    return { rows: [], problems: [{ line: first + 1, reason: 'Die erste Zeile braucht die Spaltenköpfe, mindestens „Bezeichnung“.' }], unknown };
  }
  const rows: BenchmarkInput[] = [];
  const problems: BenchmarkPaste['problems'] = [];
  for (let n = first + 1; n < lines.length; n++) {
    const line = lines[n]!;
    if (line.trim() === '') continue;
    const cells = split(line, tab);
    const row: Record<string, string | number | null> = {
      source: defaults.source ?? 'BKI', edition: defaults.edition ?? null, priceDate: defaults.priceDate ?? null,
      region: defaults.region ?? null, buildingType: defaults.buildingType ?? null, costGroup: null, label: '',
      unit: null, low: null, mid: null, high: null, license: defaults.license ?? null, note: null,
    };
    let bad: string | null = null;
    fields.forEach((f, i) => {
      if (!f) return;
      const raw = cells[i] ?? '';
      if (NUMERIC.has(f)) {
        const v = parseGermanNumber(raw);
        if (v === undefined) bad = `„${raw}“ ist keine Zahl`;
        else row[f] = v;
      } else if (raw !== '') row[f] = raw;
    });
    if (typeof row.costGroup === 'string') row.costGroup = row.costGroup.replace(/^KG\s*/i, '').trim() || null;
    if (typeof row.costGroup === 'string' && !/^\d{3}(\.\d{1,3})*$/.test(row.costGroup)) bad = `Kostengruppe „${row.costGroup}“ ist keine Nummer wie 330`;
    if (!row.label) bad = 'Bezeichnung fehlt';
    if (!row.source) bad = 'Quelle fehlt';
    if (bad) problems.push({ line: n + 1, reason: bad });
    else rows.push(row as unknown as BenchmarkInput);
  }
  return { rows, problems, unknown };
}
